import React from "react";
import { Card, Col, Row, Table } from "react-bootstrap";

/**
 * In-dashboard wiki: how the Healthcare vertical operates across Lusuku.
 *
 * IMPORTANT — content policy for this file:
 *   - No credentials, API keys, tokens, connection strings.
 *   - No internal hostnames or ports that aren't already public.
 *   - Development-only guidance must be gated behind NODE_ENV so CRA
 *     dead-code-eliminates it from the production bundle.
 */
const IS_DEV = process.env.NODE_ENV === "development";

const ACCENT = "#E91E63";
const TINT = "#E91E631a";
const PILLTEXT = "#ad1457";

const titleStyle = {
  fontSize: 13.5,
  fontWeight: 700,
  color: "#1a2b4a",
};

const bodyText = { fontSize: 12.5 };

const cardStyle = {
  border: "1px solid #e5e9f1",
  borderRadius: 8,
  boxShadow: "none",
};

const Pill = ({ label }) => (
  <span
    style={{
      display: "inline-block",
      padding: "2px 8px",
      borderRadius: 10,
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: "0.3px",
      textTransform: "uppercase",
      background: TINT,
      color: PILLTEXT,
      marginRight: 6,
      marginBottom: 4,
    }}
  >
    {label}
  </span>
);

const SectionIcon = ({ cls }) => (
  <i
    className={cls}
    style={{
      fontSize: 15,
      color: "#1a2b4a",
      marginRight: 8,
      verticalAlign: "middle",
    }}
  />
);

const QA = ({ q, a }) => (
  <div style={{ marginBottom: 10 }}>
    <div style={{ fontSize: 12.5, fontWeight: 700, color: "#1a2b4a" }}>{q}</div>
    <div style={{ fontSize: 12.5, color: "#6b7a99", marginTop: 1 }}>{a}</div>
  </div>
);

const HealthcarePlatformWiki = () => {
  return (
    <div>
      {/* Compact header */}
      <div
        style={{
          borderLeft: `3px solid ${ACCENT}`,
          paddingLeft: 12,
          marginBottom: 18,
        }}
      >
        <div style={{ fontSize: 17, fontWeight: 700, color: "#1a2b4a" }}>
          Healthcare on Lusuku
        </div>
        <div style={{ fontSize: 12.5, color: "#6b7a99", marginTop: 2 }}>
          Staff reference for pharmacies, clinics and labs — prescriptions,
          age-restricted items and healthcare working hours.
        </div>
      </div>

      {IS_DEV && (
        <div
          style={{
            background: "#fff8ea",
            border: "1px solid #f1e0a8",
            borderRadius: 8,
            padding: "10px 14px",
            marginBottom: 16,
            fontSize: 12.5,
          }}
        >
          <div style={{ fontWeight: 700, color: "#7a5a00", marginBottom: 2 }}>
            Local development only
          </div>
          <div className="text-muted">
            End-to-end local walkthrough: register a Healthcare Provider on
            the public registration form, verify it as SUPERUSER under
            Users → Sellers, sign in as the vendor, create sections and
            Rx-flagged / age-restricted / controlled items, then order from
            the Expo app and complete checkout with a prescription upload and
            age confirmation. Environment values are configured in the server
            environment, never in this UI, which is served to all supervisors
            in production.
          </div>
        </div>
      )}

      <Card className="mb-3" style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-ambulance" />
            At a glance — what makes healthcare different
          </Card.Title>
          <div className="mb-2">
            <Pill label="Rx-gated checkout" />
            <Pill label="Age restrictions" />
            <Pill label="Products + services" />
            <Pill label="Outlets" />
            <Pill label="Working hours" />
            <Pill label="Real-time status" />
          </div>
          <p className="text-muted mb-0" style={bodyText}>
            <strong>Healthcare</strong> is a first-class vendor type created
            through public registration (alongside restaurants, retailers and
            wholesalers). What sets it apart: catalogue items can require a{" "}
            <strong>prescription upload</strong> at checkout, carry{" "}
            <strong>age restrictions</strong>, be flagged as{" "}
            <strong>controlled substances</strong>, and be either physical{" "}
            <strong>PRODUCTs</strong> (stock-tracked) or appointment-based{" "}
            <strong>SERVICEs</strong> (slot-tracked). Healthcare vendors are
            shoppable on both the Expo mobile app and the web storefront. The
            vertical&rsquo;s theme colour is pink/magenta (<code>#E91E63</code>).
          </p>
        </Card.Body>
      </Card>

      <Row className="g-3 mb-3">
        <Col md={6}>
          <Card className="h-100" style={cardStyle}>
            <Card.Body>
              <Card.Title style={titleStyle}>
                <SectionIcon cls="nc-icon nc-badge" />
                1. Onboarding &amp; verification
              </Card.Title>
              <Card.Text className="text-muted mb-2" style={bodyText}>
                Healthcare vendors self-register on the public{" "}
                <code>/vendorRegistration</code> form by selecting{" "}
                <strong>&ldquo;Healthcare Provider&rdquo;</strong> as the
                vendor type. Beyond the usual name / email / phone / address,
                the form captures healthcare-specific credentials:
              </Card.Text>
              <ul
                className="mb-2 text-muted"
                style={{ ...bodyText, paddingLeft: 18 }}
              >
                <li><strong>Medical license number</strong></li>
                <li><strong>Certifications</strong> (comma-separated list)</li>
                <li>
                  <strong>Specializations</strong> — specialty tags such as
                  Pharmacy, Clinic, Lab
                </li>
                <li><strong>Type of services</strong> offered</li>
              </ul>
              <Card.Text className="text-muted mb-0" style={bodyText}>
                The account stays inactive until a <strong>SUPERUSER</strong>{" "}
                verifies it under <strong>Users → Sellers</strong>
                (&ldquo;Verify&rdquo; on the vendor row). Only then can the
                vendor sign in and manage their catalogue.
              </Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="h-100" style={cardStyle}>
            <Card.Body>
              <Card.Title style={titleStyle}>
                <SectionIcon cls="nc-icon nc-bullet-list-67" />
                2. Catalogue &amp; product rules
              </Card.Title>
              <Card.Text className="text-muted mb-2" style={bodyText}>
                Vendors organise their catalogue into <strong>sections</strong>{" "}
                (e.g. Medications, Consultations, Medical Equipment). Every
                item is one of two types:
              </Card.Text>
              <ul
                className="mb-2 text-muted"
                style={{ ...bodyText, paddingLeft: 18 }}
              >
                <li>
                  <strong>PRODUCT</strong> — physical item with a stock
                  quantity.
                </li>
                <li>
                  <strong>SERVICE</strong> — appointment-based, with available
                  slots instead of stock.
                </li>
              </ul>
              <Card.Text className="text-muted mb-0" style={bodyText}>
                Per-item compliance flags: <strong>Requires
                Prescription</strong> (shows an ℞ / &ldquo;Rx Required&rdquo;
                badge in the shopper apps), <strong>Age Restriction</strong>{" "}
                (a minimum age, rendered as an &ldquo;18+&rdquo; /
                &ldquo;21+&rdquo; badge), and <strong>Controlled
                Substance</strong>. Vendors can also upload images, assign
                categories, and enable/disable, edit or delete items.
              </Card.Text>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="mb-3" style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-mobile" />
            3. Ordering &amp; customer flow
          </Card.Title>
          <p className="text-muted mb-2" style={bodyText}>
            Customers reach healthcare vendors from the{" "}
            <strong>Healthcare</strong> card on the Expo app HomePage, or on
            the web via the healthcare listing page. On the web, healthcare
            has its <strong>own cart</strong>, kept per vendor and separate
            from the restaurant / retailer carts.
          </p>
          <ul className="mb-2 text-muted" style={{ ...bodyText, paddingLeft: 18 }}>
            <li>
              <strong>Prescription gate.</strong> If any cart item requires a
              prescription, checkout shows an upload section marked{" "}
              <strong>Required</strong> — the customer must upload a
              prescription image, and the order{" "}
              <strong>cannot be placed without it</strong>.
            </li>
            <li>
              <strong>Age gate.</strong> Age-restricted items add a
              confirmation toggle (&ldquo;I confirm I am XX+ years
              old&rdquo;); checkout validates it before allowing the order.
            </li>
            <li>
              <strong>Open/closed gate.</strong> Ordering is blocked while the
              vendor is closed. Each vendor (and each outlet independently)
              has weekly working hours — with multiple time slots per day if
              needed — plus special hours for holidays that override the
              weekly schedule, all evaluated in the vendor&rsquo;s timezone
              (default <code>Africa/Kampala</code>). New vendors default to
              Mon–Sat 08:00–18:00, Sunday closed. Cards show a colour-coded
              status (green open, red closed) and, when closed, the next
              opening time (looked up to 7 days ahead).
            </li>
            <li>
              <strong>Live updates.</strong> Hours and status changes push to
              all clients in real time over socket.io
              (<code>healthcareHoursUpdated</code> /{" "}
              <code>outletHoursUpdated</code>) — no refresh needed.
            </li>
          </ul>
          <p className="text-muted mb-0" style={bodyText}>
            A vendor may run multiple <strong>outlets</strong>, each with
            independently-managed hours; vendor cards show up to three outlets
            with a &ldquo;+X more&rdquo; indicator.
          </p>
        </Card.Body>
      </Card>

      <Card className="mb-3" style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-delivery-fast" />
            4. Order lifecycle &amp; delivery
          </Card.Title>
          <p className="text-muted mb-2" style={bodyText}>
            Healthcare orders ride the same state machine as every other order
            on Lusuku: <strong>PLACED → CONFIRMED → READY → ASSIGNED →
            PICKED_UP → DELIVERED → SETTLED</strong>. The healthcare-specific
            extra is that the <strong>prescription image is attached to the
            order</strong>, so staff and the vendor can verify it before
            dispensing.
          </p>
          <ul className="mb-0 text-muted" style={{ ...bodyText, paddingLeft: 18 }}>
            <li>
              <strong>Fulfilment.</strong> The platform&rsquo;s three delivery
              modes all apply: the vendor&rsquo;s own riders, 3PL partner
              riders, or Lusuku dispatch. Healthcare vendors manage their own
              rider roster through vendor-scoped rider endpoints guarded by
              healthcare vendor authorization.
            </li>
            <li>
              <strong>Delivery fees.</strong> Quotes are server-authoritative
              and signed — client-side amounts are display-only.
            </li>
            <li>
              <strong>Delivery zone.</strong> The healthcare vendor profile
              carries a delivery zone radius (default 5&nbsp;km).
            </li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-3" style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-money-coins" />
            5. Money flow
          </Card.Title>
          <ul className="mb-0 text-muted" style={{ ...bodyText, paddingLeft: 18 }}>
            <li>
              <strong>Payment methods.</strong> Mobile money and card via
              Flutterwave, or cash on delivery — the same rails as the rest of
              the platform. All amounts are computed server-side in UGX.
            </li>
            <li>
              <strong>Escrow.</strong> Paid funds are held by the Wallet
              Service; the vendor balance shows <em>pending</em> until
              delivery is confirmed.
            </li>
            <li>
              <strong>Commission.</strong> On <code>SETTLED</code> the
              platform commission is deducted and the vendor&rsquo;s released
              balance increments — no healthcare-specific exception to the
              standard split.
            </li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-3" style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-app" />
            6. Dashboard surface area
          </Card.Title>
          <p className="text-muted mb-3" style={bodyText}>
            Where healthcare lives in this dashboard — admin-side management
            plus the vendor&rsquo;s own screens after login.
          </p>
          <Table responsive hover size="sm" className="mb-0" style={{ fontSize: 12 }}>
            <thead style={{ background: "#f5f7fb" }}>
              <tr>
                <th>Surface</th>
                <th>Where</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Healthcare vendors (admin)</td>
                <td>Healthcare view in the admin sidebar</td>
                <td className="text-muted">
                  View/edit vendors, manage outlets, configure hours (24/7,
                  Weekdays 9-5, custom slots) and special hours
                </td>
              </tr>
              <tr>
                <td>Vendor dashboard</td>
                <td><code>/admin/healthcare-dashboard</code></td>
                <td className="text-muted">
                  Stats: sections, products, services, prescription items
                </td>
              </tr>
              <tr>
                <td>Products &amp; services</td>
                <td><code>/admin/healthcare-menu</code></td>
                <td className="text-muted">
                  Sections + item CRUD, under HEALTHCARE in the sidebar
                </td>
              </tr>
              <tr>
                <td>Create / edit item</td>
                <td>
                  <code>/admin/create-healthcare-product</code>,{" "}
                  <code>/admin/edit-healthcare-product/:productId</code>
                </td>
                <td className="text-muted">
                  Rx / age / controlled-substance flags set here
                </td>
              </tr>
              <tr>
                <td>Vendor profile</td>
                <td><code>/admin/healthcare-account</code></td>
                <td className="text-muted">
                  License, certifications, specializations, activation toggle
                </td>
              </tr>
              <tr>
                <td>Registration &amp; verification</td>
                <td>
                  <code>/vendorRegistration</code>, Users → Sellers
                </td>
                <td className="text-muted">Public signup; SUPERUSER verifies</td>
              </tr>
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Card style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-bulb-63" />
            7. Quick answers
          </Card.Title>
          <QA
            q="A customer&rsquo;s checkout is blocked on a pharmacy item — why?"
            a="Most likely a compliance gate: an item flagged Requires Prescription needs a prescription image uploaded, an age-restricted item needs the age confirmation toggled, or the vendor is currently closed. All three block order placement by design."
          />
          <QA
            q="A new healthcare vendor can&rsquo;t sign in — is something broken?"
            a="No — accounts stay inactive until a SUPERUSER verifies the vendor under Users → Sellers. Verify the row and ask them to sign in again."
          />
          <QA
            q="A vendor set 24/7 hours but still shows Closed."
            a="Re-save the hours from the admin Healthcare view and confirm every day is marked open with a full-day slot, and that the vendor&rsquo;s timezone is correct (default Africa/Kampala). Saved changes push live to all apps over socket.io — no restart or refresh needed."
          />
          <QA
            q="Where do I find the prescription for an order?"
            a="The uploaded prescription image is attached to the order record itself — open the order to view it and verify before dispensing."
          />
          <QA
            q="An outlet shows different hours than the main store — is that a bug?"
            a="No. Every outlet carries its own independent weekly and special hours; the vendor card shows up to three outlets with a &ldquo;+X more&rdquo; indicator."
          />
          <QA
            q="How far will a healthcare vendor deliver?"
            a="Each vendor profile carries a delivery zone radius, default 5 km. It can be adjusted on the vendor&rsquo;s profile."
          />
          <QA
            q="A vendor&rsquo;s stats dashboard shows zeros everywhere."
            a="Usually just an empty catalogue: sections must be created first, then items added to them. Refresh the dashboard after the first items are in."
          />
        </Card.Body>
      </Card>
    </div>
  );
};

export default HealthcarePlatformWiki;
